import { useState, useEffect, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { getWalletOnboardingUrlAPI, fetchStripeAccount } from "services/api/haulpay";
import { showErrorMessage } from "helpers/toastMessage";
import onboardingStatus from "helpers/walletOnboardingStatus";

export type AccountSettings = {
  deposit_enabled: boolean;
  withdrawal_enabled: boolean;
  bank_account_linked: boolean;
  onboarding_required: boolean;
  onboarding_status: string;
  id: string;
};

export type StripeOnboarding = {
  accountSettings: AccountSettings;
  handleContinue: () => Promise<void>;
  statusMapping: {
    [key: string]: {
      text: string;
      alertType: string;
      icon: string;
      color: string;
    };
  };
  isGenerateLink: boolean;
};

const useStripeOnboarding = (): StripeOnboarding => {
  const [accountSettings, setAccountSettings] = useState<AccountSettings>({
    deposit_enabled: false,
    withdrawal_enabled: false,
    bank_account_linked: false,
    onboarding_required: false,
    onboarding_status: "",
    id: ""
  });

  const [isGenerateLink, setIsGenerateLink] = useState(false);

  const { data, error, isSuccess } = useQuery(["fetch_stripe_account"], async () => {
    const response = await fetchStripeAccount();
    return response.data;
  });

  useEffect(() => {
    if (isSuccess && data) {
      const {
        deposit_enabled,
        withdrawal_enabled,
        bank_account_linked,
        onboarding_required,
        onboarding_status,
        id
      } = data;

      setAccountSettings({
        deposit_enabled,
        withdrawal_enabled,
        bank_account_linked,
        onboarding_required,
        onboarding_status,
        id
      });
    }
  }, [isSuccess, data]);

  // Handle error
  useEffect(() => {
    if (error instanceof Error) {
      showErrorMessage(`Error fetching Stripe account: ${error.message}`);
    }
  }, [error]);

  const handleContinue = useCallback(async () => {
    try {
      setIsGenerateLink(true);
      const res = await getWalletOnboardingUrlAPI();
      window.location.href = res.data?.url;
    } catch (error: unknown) {
      let errorMessage = "";
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      showErrorMessage(`Error generating wallet onboarding URL: ${errorMessage}`);
    } finally {
      setTimeout(() => setIsGenerateLink(false), 200);
    }
  }, []);

  const statusMapping = {
    [onboardingStatus.ONBOARDING_REVIEW]: {
      text: "Onboarding process still in review.",
      alertType: "info",
      icon: "find_in_page",
      color: "text-blue-500" // Tailwind color class for "info"
    },
    [onboardingStatus.PENDING]: {
      text: "Onboarding process still pending.",
      alertType: "warning",
      icon: "settings_applications",
      color: "text-yellow-500" // Tailwind color class for "warning"
    },
    [onboardingStatus.DELETED]: {
      text: "You cannot use this feature please contact support@haulpay.io.",
      alertType: "danger",
      icon: "error",
      color: "text-red-500" // Tailwind color class for "danger"
    }
  };

  return { accountSettings, handleContinue, statusMapping, isGenerateLink };
};

export default useStripeOnboarding;
